import { computed, ref } from 'vue'

export const POLL_INTERVAL_MS = 2500
export const MAX_POLL_FAILURES = 5

const STATUS_PATH = 'franchise/menu/status'

// Module scope, like importRunTracker: the menu pages remount their header on every location switch,
// and the last status keeps showing while the next one loads.
const status = ref(null)
const lost = ref(false)

let timer = null
let generation = 0
// True while a request is out or the next one is due.
let tracking = false

export const isTrackingMenuSync = () => tracking

// Stops polling; any response still in flight is ignored. The last status stays on show.
export function stopMenuSync() {
  generation += 1
  tracking = false
  if (timer) clearTimeout(timer)
  timer = null
}

export function clearMenuSync() {
  stopMenuSync()
  status.value = null
  lost.value = false
}

// Fetches franchise/menu/status now (or, handed a status, after the interval while that one runs),
// then again every POLL_INTERVAL_MS while the full sync runs. onStatus gets each status received.
export function trackMenuSync({ call, initial = null, onStatus = () => {} }) {
  stopMenuSync()
  const mine = generation
  let failures = 0
  lost.value = false
  tracking = true

  const schedule = () => (timer = setTimeout(poll, POLL_INTERVAL_MS))
  const finish = () => (tracking = false)

  const poll = async () => {
    if (mine !== generation) return
    try {
      const response = await call('GET', STATUS_PATH, {}, { receive: false })
      if (mine !== generation) return
      failures = 0
      status.value = response.data
      onStatus(response.data)
      if (response.data?.running) schedule()
      else finish()
    } catch {
      if (mine !== generation) return
      failures += 1
      // A dropped connection says nothing about the sync, so this stops watching rather than claim it failed.
      if (failures < MAX_POLL_FAILURES) return schedule()
      lost.value = true
      finish()
    }
  }

  if (!initial) return poll()
  status.value = initial
  if (initial.running) schedule()
  else finish()
}

const countErrors = locations => (locations ?? []).reduce((sum, location) => sum + (location.errors?.length ?? 0), 0)

export function useMenuSyncTracker() {
  const errorCount = computed(() => countErrors(status.value?.locations))
  const running = computed(() => !lost.value && !!status.value?.running)
  // 'lost' | 'syncing' | 'errors' | 'synced', or null before any status
  const phase = computed(() => {
    if (lost.value) return 'lost'
    if (!status.value) return null
    if (status.value.running) return 'syncing'
    return errorCount.value ? 'errors' : 'synced'
  })
  return { status, lost, running, errorCount, phase, stop: stopMenuSync }
}
